import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsMore from 'highcharts/highcharts-more';

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import ChartPanel from '../FunViz/components/ChartPanel';
import CustomRangeControl from '../FunViz/components/CustomRangeControl';
import SymbolSearch from '../FunViz/components/SymbolSearch';
import ControlsPanel from '../FunViz/components/ControlsPanel';
import StatsPanel from '../FunViz/components/StatsPanel';
import PerformancePopup from '../FunViz/components/PerformancePopup';
import TrendingPanel from '../FunViz/components/TrendingPanel';
import RatioChartCard from '../FunViz/components/RatioChartCard';
import ScoresCard from '../FunViz/components/ScoresCard';
import ValuationSubsequentReturnsCard from '../FunViz/components/ValuationSubsequentReturnsCard';
import IncomeStatementSankeyCard from '../FunViz/components/IncomeStatementSankeyCard';
import CollapsibleCard, { readCardOpen } from '../FunViz/components/CollapsibleCard';
import DisclaimerFooter from '../FunViz/components/DisclaimerFooter';
import Spinner from '../common/Spinner';

import useChartInteraction from '../FunViz/hooks/useChartInteraction';
import { useStockData } from '../FunViz/hooks/useStockData';
import { useValuationRegression } from '../FunViz/hooks/useValuationRegression';
import { useMetricCorrelationTable } from '../FunViz/hooks/useMetricCorrelationTable';
import useAxisSync from '../FunViz/hooks/useAxisSync';
import { useSymbolSearch } from '../FunViz/hooks/useSymbolSearch';
import { fetchTrending } from '../FunViz/data/dataService';
import { defaultMultiple, MAIN_CHART_METRIC_OPTIONS } from '../FunViz/config/metricsConfig';
import { buildRatioSeries, lastNonNullPoint, lastNonNullAsOf } from '../FunViz/hooks/seriesMath';

import styles from '../FunViz/FunViz.module.css';

// The ratio/main chart overlay bands use Highcharts' `arearange` series type,
// which only "highstock" doesn't include — register it once at module load.
HighchartsMore(Highcharts);

/**
 * Reads a `[x, y][]` series at `asOfTs`: the y on the last point with
 * `t <= asOfTs`, without walking further back when that point is null.
 * Key Facts' dividend yield uses this so a post-stop $0 dividend (a gap in
 * buildRatioSeries) shows unavailable, matching the screener — unlike
 * lastNonNullAsOf, which would keep surfacing the last pre-stop yield.
 */
const valueAtAsOf = (series = [], asOfTs) => {
  if (asOfTs == null) return null;
  let value = null;
  for (const [t, v] of series) {
    if (t == null || t > asOfTs) break;
    value = v;
  }
  return value != null && Number.isFinite(value) ? value : null;
};

/** Mirrors FunViz.module.css: stacked column layout, except narrow landscape phones. */
const isStackedControlsLayout = () => {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth > 768) return false;
  const isLandscapeShortPhone = typeof window.matchMedia === 'function'
    && window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches;
  return !isLandscapeShortPhone;
};

// This component is the relocated body of the former FunViz.js (the page
// that used to render at /funviz directly). It is now the Analysis tab's
// content, rendered inside FunVizTabs' <Outlet/>. FunVizTabs now renders
// only the page-level container and a standalone tab bar above the content
// (no shared white card), so this component restores its own pre-split
// layout: the Trending + chart + controls block lives in its own .main
// white card, and RatioChartCard / ValuationSubsequentReturnsCard render as
// their own separate cards below it. Everything else is unchanged behavior
// from FunViz.js.
const Analysis = () => {
  const {
    before, after, change, changeAnn, pointCount,
    handlePointClick, resetPoints,
  } = useChartInteraction();

  const {
    status, symbolActive, companyNameActive, setCompanyNameActive, profileActive,
    fetchData, chartOptions, ratioChartOptions, valuationInputs, incomeStatement, qualityInputs, ratios, defaultRange, chartRef, ratioChartRef,
    applyTickedMetrics, applyRatioMode, applyExcludeLossPeriods, applyMetric, applyMultiple,
    applyValueBasis, applyAdjustForSbc, applyDisplayScale, applyAxisScale,
    recomputeMainOverlaysForWindow, recomputeRatioOverlaysForWindow,
  } = useStockData(handlePointClick);

  const {
    symbol,
    suggestions, showSuggestions, setShowSuggestions, searchFailed,
    handleSymbolChange, handleSuggestionSelect,
    setSymbol, ensureCompanyName,
  } = useSymbolSearch();

  // `multiple` always holds the EFFECTIVE numeric multiple the chart uses, so
  // every downstream consumer (fetchData, applyMetric, keyFacts, StatsPanel)
  // just reads it regardless of mode. `multipleMode` picks where that value
  // comes from: 'default' keeps it in sync with the hardcoded per-metric/
  // anchor multiple (defaultMultiple), 'custom' holds the user's committed
  // value from the Multiple dropdown. Metric/anchor changes reset to
  // 'default' (a custom P/E is meaningless as a P/S) — see handleMetricChange
  // / handleValueBasisChange.
  const [multipleMode, setMultipleMode] = useState('default');
  const [multiple, setMultiple] = useState(15);
  const [metric, setMetric] = useState('eps');
  const [valueBasis, setValueBasis] = useState('marketCap');
  const [displayScale, setDisplayScale] = useState('perShare');
  const [axisScale, setAxisScale] = useState('linear');
  const [ratioMode, setRatioMode] = useState('ratio');
  const [excludeLossPeriods, setExcludeLossPeriods] = useState(false);
  // "Adjust for SBC" (2026-10-30, metrics.sbc) — affects FCF/OCF display
  // only; defaults off so nobody who doesn't touch the toggle sees any
  // behavior change. See useStockData.js's applyAdjustForSbc.
  const [adjustForSbc, setAdjustForSbc] = useState(false);
  const [tickedMetrics, setTickedMetrics] = useState(['eps']);
  const [trending, setTrending] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  // Change 1 (collapsible controls pane): opt-in affordance, defaults to the
  // pre-existing expanded layout so nobody who doesn't touch the new toggle
  // sees any behavior change.
  const [controlsCollapsed, setControlsCollapsed] = useState(false);
  // Change 2 (controls pane height cap): tracks chartSection's own rendered
  // height so controlsSection can be capped to match it (see the
  // ResizeObserver effect below) instead of letting a tall StatsPanel/
  // ControlsPanel stretch the whole `.main` card.
  const [controlsMaxHeight, setControlsMaxHeight] = useState(null);
  // The "Valuation vs. Subsequent Returns" card's single-select metric. It
  // seeds from the main chart's `metric` and is kept in sync when the main
  // chart's metric changes (see handleMetricChange), so picking a metric up
  // top drives this card too. It can still be changed independently via the
  // card's own dropdown without affecting the main chart.
  const [valuationMetric, setValuationMetric] = useState(metric);
  // The card's second selector: NTM (default, matches prior behavior) vs
  // TTM — see valuationRegression.js's module docstring `period` section.
  const [valuationPeriod, setValuationPeriod] = useState('ntm');

  // The Valuation and Sankey cards are collapsed by default (the page felt
  // heavy with everything mounted at once). `valuationOpen` additionally gates
  // the two expensive regression hooks below — they return null while the card
  // is collapsed, so a symbol load doesn't pay for ~32 regressions nobody is
  // looking at. Seeded from the SAME persisted value CollapsibleCard reads, so
  // they agree on the first render; CollapsibleCard reports later toggles via
  // its onToggle callback.
  const [valuationOpen, setValuationOpen] = useState(() => readCardOpen('funviz.card.valuation', false));

  // Income-Statement Sankey card: which cadence (Annual/Quarterly) and which
  // period index within it. The index is reset to the latest period whenever
  // the symbol's data changes (incomeStatement identity changes only on fetch)
  // OR the cadence is toggled — both handled by the effect below, so a symbol
  // switch and a cadence switch both land on the most recent period.
  const [incomeCadence, setIncomeCadence] = useState('annual');
  const [incomeIndex, setIncomeIndex] = useState(0);
  useEffect(() => {
    const periods = incomeStatement[incomeCadence] || [];
    setIncomeIndex(periods.length ? periods.length - 1 : 0);
  }, [incomeStatement, incomeCadence]);
  const incomePeriods = incomeStatement[incomeCadence] || [];
  const handleIncomeCadenceChange = useCallback((v) => setIncomeCadence(v), []);
  const handleIncomeSelect = useCallback((i) => setIncomeIndex(i), []);
  const handleIncomePrev = useCallback(() => setIncomeIndex((i) => Math.max(0, i - 1)), []);
  const handleIncomeNext = useCallback(
    () => setIncomeIndex((i) => {
      const len = (incomeStatement[incomeCadence] || []).length;
      return len ? Math.min(len - 1, i + 1) : 0;
    }),
    [incomeStatement, incomeCadence],
  );

  const {
    horizon, setHorizon, lookback, setLookback, resultsByHorizon,
  } = useValuationRegression(
    valuationInputs.price, valuationInputs.shares, valuationInputs.epsQuarterly,
    valuationInputs.metricSeriesByMetric, valuationMetric, valuationPeriod,
    // The card follows the page's live top-of-page valueBasis toggle (same
    // pattern the main chart uses), rather than owning a separate basis
    // selector — the user's mental model is "the toggle at the top drives the
    // page". netDebt is only consumed under the Enterprise Value basis.
    valueBasis, valuationInputs.netDebt,
    // `enabled`: skip the per-horizon regression sweep while the card is collapsed.
    valuationOpen,
  );

  // Every metric x fixed-horizon combination's fit strength/direction, for
  // the card's "best predictor at a glance" table — independent of
  // `valuationMetric` (which only controls the chart above it), so it does
  // not need to be recomputed on metric-dropdown changes. It DOES share
  // `lookback`, `valuationPeriod`, and `valueBasis` with the chart above it,
  // so the table's ranking reflects the same evaluation window, multiple
  // vintage (NTM/TTM), and value basis the user currently has selected.
  const correlationRows = useMetricCorrelationTable(
    valuationInputs.price, valuationInputs.shares, valuationInputs.epsQuarterly,
    valuationInputs.metricSeriesByMetric, lookback, valuationPeriod, valueBasis, valuationInputs.netDebt,
    // `enabled`: skip the 32-regression sweep while the card is collapsed.
    valuationOpen,
  );

  // Live figures for StatsPanel's Key Facts rows, derived here rather than
  // plumbed out of useStockData: `valuationInputs`, `metric` and `valueBasis`
  // are already reactive state, so a useMemo tracks them automatically —
  // whereas threading two more values out of the hook would mean setting
  // them in all six of its rebuild paths (fetch + the five apply*
  // callbacks) and keeping those in sync forever.
  //
  // Both come off buildRatioSeries — the SAME builder the ratio chart and
  // the hover tooltip's per-date "Price (P/E 24.3)" figures use — read via
  // lastNonNullAsOf at the last real price date, so the panel shows the
  // current blended figure rather than the furthest-out estimate. (The main-
  // chart legend shows plain names only; it no longer carries a bracketed
  // figure this could drift from.) `excludeLossPeriods` is pinned false and
  // ratioMode to 'ratio' for the same reason buildMainChartSeries does: the
  // valuation multiple is always on the multiple basis, independent of the
  // lower chart's Ratio/Yield toggle.
  const keyFacts = useMemo(() => {
    const { price = [], shares = [], netDebt = [], metricSeriesByMetric = {} } = valuationInputs;

    // Reference date = the last REAL price point. Both figures are read at
    // this date rather than at the end of the ratio series: buildRatioSeries
    // spans the metric's own forward timestamps too (price flat-held past its
    // last actual point), so reading the series' final value would give the
    // ratio against the furthest-out estimate (VZ: 2030) instead of today's.
    const asOfTs = lastNonNullPoint(price)?.[0];

    // Both figures are BLENDED, and come straight off the ratio chart's own
    // series builder — no separate math here. buildRatioSeries interpJoins the
    // metric onto price's dense daily grid, so its value at `asOfTs` is the
    // linear blend between the two metric points surrounding that date. The
    // backend orders the metric series actuals -> TTM bridge -> forward
    // estimates chronologically, so the preceding point is automatically
    // whichever of {last actual FY, TTM} is NEWER (for VZ: the 2026-06-30 TTM,
    // not the 2025-12-31 FY) and the following point is the nearest consensus
    // estimate. That's the blend, with no conditional needed.
    const currentRatio = lastNonNullAsOf(
      buildRatioSeries(
        price, shares, netDebt, valueBasis, metric, metricSeriesByMetric[metric] || [], 'ratio', false,
      ),
      asOfTs,
    );

    // Dividend yield is the same builder in 'yield' mode (metric ÷ value, as a
    // percent), pinned to the marketCap basis: a dividend yield is quoted
    // against price by convention, never against enterprise value, so it must
    // NOT follow the page's Market Cap/EV toggle the way the multiple above
    // does. Scale-invariant, so the Per Share/Total toggle doesn't affect it.
    const dividendYield = valueAtAsOf(
      buildRatioSeries(
        price, shares, netDebt, 'marketCap', 'dividends', metricSeriesByMetric.dividends || [], 'yield', false,
      ),
      asOfTs,
    );

    return { currentRatio, dividendYield };
  }, [valuationInputs, metric, valueBasis]);

  // Keeps the address bar in sync with the loaded symbol via `?symbol=SYM`,
  // the same query-param convention the Screener uses to link here. Called
  // from the two paths that actually load a stock — handleSubmit (the Go
  // button / Enter) and handleTrendingSelect — so a shared/bookmarked/
  // refreshed URL reloads the stock you were looking at rather than snapping
  // back to the arrival symbol (or AAPL). Uses replaceState, not pushState,
  // so switching stocks doesn't pile up back-button history entries. Preserves
  // the current pathname (the tab route, `/analysis`) rather than hardcoding it,
  // and is a no-op-safe guard in the standalone-render tests where history may
  // be a jsdom stub. The mount-time reader (see the initial-fetch effect) only
  // reads `?symbol=` once, so rewriting it here never re-triggers a fetch.
  const syncUrlSymbol = useCallback((sym) => {
    const clean = (sym || '').trim().toUpperCase();
    if (!clean) return;
    const url = `${window.location.pathname}?symbol=${encodeURIComponent(clean)}`;
    window.history.replaceState(window.history.state, '', url);
  }, []);

  const searchRef = useRef(null);
  const chartSectionRef = useRef(null);
  const initialized = useRef(false);
  const submitParamsRef = useRef({ symbol, multiple, metric, valueBasis, displayScale, axisScale, ratioMode, excludeLossPeriods, tickedMetrics, adjustForSbc });
  submitParamsRef.current = { symbol, multiple, metric, valueBasis, displayScale, axisScale, ratioMode, excludeLossPeriods, tickedMetrics, adjustForSbc };
  // Handed to every fetchData call so a toggle flipped while a stock is still
  // loading is honoured when its data arrives (see fetchData's getControls).
  const getControls = useCallback(() => submitParamsRef.current, []);

  // The two charts' windows are independent (see hooks/useAxisSync.js), so
  // each gets its own recompute handler: zooming the main chart recomputes
  // only its fair-value overlay, zooming the ratio chart recomputes only its
  // per-metric overlays and y-axis. Read tickedMetrics via the ref so these
  // callbacks' identities stay stable (useAxisSync only binds them once, at
  // chart creation).
  const handleMainWindowChange = useCallback((min, max) => {
    recomputeMainOverlaysForWindow(min, max);
  }, [recomputeMainOverlaysForWindow]);

  const handleRatioWindowChange = useCallback((min, max) => {
    recomputeRatioOverlaysForWindow(submitParamsRef.current.tickedMetrics, min, max);
  }, [recomputeRatioOverlaysForWindow]);

  const { mainChartCallback, ratioChartCallback } = useAxisSync(
    chartRef, ratioChartRef, handleMainWindowChange, handleRatioWindowChange,
  );

  // Initial data fetch on mount. Defaults to AAPL, unless the URL carries a
  // `?symbol=` query param — the Screener tab's row-click navigation links
  // here (see Screener.js) as `/funviz?symbol=<SYM>` to open the Analysis
  // tab pre-loaded with that row's symbol. There was no pre-existing
  // "select a symbol from the URL" mechanism in this codebase to reuse, so
  // this establishes the minimal one: a plain query param, read directly
  // from `window.location` rather than via react-router-dom's
  // `useSearchParams` so this component keeps working when rendered without
  // a Router in tests (Analysis.test.js and Analysis.errorBoundary.test.js
  // render it standalone, not under a route).
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      const urlSymbol = new URLSearchParams(window.location.search).get('symbol');
      const initialSymbol = (urlSymbol || '').trim().toUpperCase();

      // A bare visit (no `?symbol=`) loads AAPL by default — the canonical
      // landing state for /funviz. A `?symbol=` (e.g. a Screener row click)
      // loads that symbol instead. Either way we resolve the real name before
      // fetching so the stats panel never shows a placeholder company: the
      // search-box/display state starts EMPTY (see useSymbolSearch/useStockData)
      // rather than pre-seeded with Apple, which is what used to make the name
      // field + ticker flash "Apple Inc."/"AAPL" when arriving from the Screener
      // on a different stock before its fetch settled.
      const symbolToLoad = initialSymbol || 'AAPL';
      setSymbol(symbolToLoad);

      // Optional `?metric=` (the Screener sets `ocf` for REIT rows). Unknown
      // values fall back to the default 'eps'.
      const urlMetric = new URLSearchParams(window.location.search).get('metric');
      const initialMetric = MAIN_CHART_METRIC_OPTIONS.some(({ value }) => value === urlMetric)
        ? urlMetric : 'eps';
      const initialMultiple = defaultMultiple(initialMetric, 'marketCap');
      if (initialMetric !== 'eps') {
        setMetric(initialMetric);
        setMultiple(initialMultiple);
        setTickedMetrics([initialMetric]);
        setValuationMetric(initialMetric);
      }
      Promise.all([
        fetchData({
          symbol: symbolToLoad, multiple: initialMultiple, companyName: symbolToLoad, metric: initialMetric,
          valueBasis: 'marketCap', displayScale: 'perShare', ratioMode: 'ratio',
          tickedMetrics: [initialMetric], getControls,
        }),
        ensureCompanyName(symbolToLoad),
      ]).then(([, name]) => { setCompanyNameActive(name); });
      fetchTrending().then(data => { setTrending(data); setTrendingLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  // After every full chart rebuild (mount, symbol switch, valueBasis/
  // displayScale/ratioMode change, ticked-set toggle), re-read each chart's
  // own real settled extremes and recompute its overlays for them — safe
  // because HighchartsReact's chart.update() runs in a useLayoutEffect,
  // which commits before this plain useEffect. Read per-chart (not the main
  // chart's window for both) so a rebuild preserves each chart's independent
  // window rather than snapping the ratio chart's overlays to the main
  // chart's window.
  useEffect(() => {
    const main = chartRef.current?.chart;
    const ratio = ratioChartRef.current?.chart;
    if (main) {
      const { min, max } = main.xAxis[0].getExtremes();
      recomputeMainOverlaysForWindow(min, max);
    }
    if (ratio) {
      const { min, max } = ratio.xAxis[0].getExtremes();
      recomputeRatioOverlaysForWindow(submitParamsRef.current.tickedMetrics, min, max);
    }
  }, [chartOptions, ratioChartOptions, chartRef, ratioChartRef, recomputeMainOverlaysForWindow, recomputeRatioOverlaysForWindow]);

  // Click-outside handler for suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [setShowSuggestions]);

  // Change 2: cap controlsSection's height to chartSection's own rendered
  // height, so a tall StatsPanel/ControlsPanel no longer inflates the whole
  // `.main` card past the chart. chartSection's height itself is
  // viewport-width-responsive (chartConfig.js's `chart.height: '65%'`, plus
  // its own breakpoint overrides), so a static CSS value can't track it —
  // this measures the live DOM box instead. ResizeObserver (rather than a
  // plain window `resize` listener) is used because it fires on the actual
  // observed element's box-size changes, which covers both viewport resizes
  // and any other reflow of chartSection's content (e.g. Highcharts'
  // internal layout settling after a chart rebuild) without needing to hook
  // into Highcharts' own resize events. No other component in this codebase
  // already uses ResizeObserver (only plain `window.innerWidth`/
  // `innerHeight` reads in the chart config files), so this doesn't need to
  // match an existing convention beyond that.
  useEffect(() => {
    const el = chartSectionRef.current;
    if (!el) return undefined;

    // Only a side-by-side `.content` row (desktop / wide-viewport layout,
    // including narrow landscape phones) has a "shorter chart column with
    // dead space next to a taller controls column" problem for this cap to
    // solve — FunViz.module.css stacks the two sections into a column at
    // max-width 768px except for orientation:landscape + max-height:500px,
    // so isStackedControlsLayout() tracks that same breakpoint pair. The
    // landscape row keeps align-items:flex-start so this height is the chart
    // column's intrinsic stack, not a stretch match to the taller controls.
    const measure = () => {
      if (controlsCollapsed || isStackedControlsLayout()) {
        setControlsMaxHeight(null);
        return;
      }
      setControlsMaxHeight(el.getBoundingClientRect().height);
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    window.addEventListener('resize', measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [controlsCollapsed]);

  // The Multiple dropdown commits ON COLLAPSE (not per keystroke — no debounce
  // needed), handing back the resolved (mode, value): 'default' with the
  // metric/anchor's hardcoded multiple, or 'custom' with the user's value
  // (already validated numeric + non-empty-fallback inside MultipleDropdown).
  // Either way `value` is the effective numeric multiple, so this just stores
  // it and rebuilds the main chart's fair-value line client-side — the
  // multiple is a scalar the line is scaled by, no refetch. No-op guard: skip
  // the rebuild if neither mode nor value actually changed.
  const handleMultipleCommit = useCallback((nextMode, nextValue) => {
    if (nextMode === multipleMode && nextValue === multiple) return;
    setMultipleMode(nextMode);
    setMultiple(nextValue);
    applyMultiple(nextValue);
  }, [applyMultiple, multipleMode, multiple]);

  // Switching metric or anchor (valueBasis) resets the multiple to a
  // sensible default for that combination — displayScale alone doesn't,
  // since the ratio's numeric value is scale-invariant.
  //
  // The main-chart metric also drives the two downstream views so the whole
  // page tracks one selection (per product: picking a metric up top should
  // reflect everywhere): the ratio/yield chart's ticked set is replaced with
  // just this metric (client-side rebuild via applyTickedMetrics, no refetch),
  // and the Valuation vs. Subsequent Returns card's own metric is set to match.
  const handleMetricChange = useCallback((event) => {
    const newMetric = event.target.value;
    // Switching metric snaps the multiple back to Default mode: a custom value
    // entered as a P/E (e.g. 22) is meaningless as a P/S, so we don't carry it
    // across metrics. The effective multiple becomes this metric's hardcoded
    // default for the current anchor.
    const newMultiple = defaultMultiple(newMetric, submitParamsRef.current.valueBasis);
    setMetric(newMetric);
    setMultipleMode('default');
    setMultiple(newMultiple);

    const nextTicked = [newMetric];
    setTickedMetrics(nextTicked);
    setValuationMetric(newMetric);

    // Live client-side rebuild — metric is derivable from the already-fetched
    // data (see applyMetric), so switching it no longer waits for a Go/Enter
    // refetch. A metric switch invalidates any drawn click-to-measure line and
    // its Before/After stats (they were captured against the previous metric's
    // fair-value line), so wipe them alongside the rebuild — matching
    // handleValueBasisChange. applyMetric rebuilds both charts (the new
    // multiple threaded in, since setMultiple isn't visible this tick), so a
    // separate applyTickedMetrics call is redundant.
    resetPoints();
    applyMetric(newMetric, newMultiple, nextTicked);
  }, [applyMetric, resetPoints]);

  // The valuation card's own metric selection — unlike handleMetricChange,
  // there's no "multiple" concept on this card, so no default-reset side
  // effect is needed here.
  const handleValuationMetricChange = useCallback((event) => {
    setValuationMetric(event.target.value);
  }, []);

  const handleValuationPeriodChange = useCallback((event) => {
    setValuationPeriod(event.target.value);
  }, []);

  // Client-side rebuild, like handleAxisScaleChange — switching the company
  // value basis is a re-derivation of already-fetched actuals (EV = market
  // cap + net debt), so no refetch is needed. The multiple resets to the new
  // basis's sensible default; that same value is threaded into the live
  // rebuild (setMultiple's async state update wouldn't be visible to
  // applyValueBasis this tick). tickedMetrics is read via the ref, matching
  // the pattern the other client-side rebuild handlers use.
  const handleValueBasisChange = useCallback((value) => {
    const newMultiple = defaultMultiple(submitParamsRef.current.metric, value);
    setValueBasis(value);
    // Same as handleMetricChange: switching the anchor (P/E vs EV/E) snaps the
    // multiple back to Default — a custom market-cap multiple isn't meaningful
    // against enterprise value.
    setMultipleMode('default');
    setMultiple(newMultiple);
    // The click-to-measure Before/After/Change stats were captured in the
    // previous basis's units (e.g. per-share Price vs total EV), so they no
    // longer describe the rebuilt actual line — wipe them. The rebuild below
    // already clears the drawn performance-line via its empty placeholder
    // series; this clears the matching PerformancePopup numbers.
    resetPoints();
    applyValueBasis(value, newMultiple, submitParamsRef.current.tickedMetrics);
  }, [applyValueBasis, resetPoints]);

  // Client-side rebuild, like handleAxisScaleChange — per-share/total is a
  // pure units re-derivation of the already-fetched actuals, no refetch.
  const handleDisplayScaleChange = useCallback((value) => {
    setDisplayScale(value);
    // Same as valueBasis: a measurement taken in per-share units is
    // meaningless once the line is redrawn in total units (and vice versa),
    // so wipe the stats alongside the rebuild.
    resetPoints();
    applyDisplayScale(value, submitParamsRef.current.tickedMetrics);
  }, [applyDisplayScale, resetPoints]);

  // Linear/log toggle for the main chart's price axis — purely a client-side
  // axis rendering change, so it rebuilds only the main chart's yAxis via the
  // hook rather than triggering a refetch.
  const handleAxisScaleChange = useCallback((value) => {
    setAxisScale(value);
    applyAxisScale(value);
  }, [applyAxisScale]);

  // Client-side rebuild, like handleTickedMetricsChange -- no refetch needed,
  // just a re-derivation of the already-fetched actuals under the new mode.
  // Reads tickedMetrics via the ref (not the closed-over state) for the same
  // reason handleSubmit does: keeps this callback's identity stable so
  // RatioChartCard's memo isn't defeated by unrelated ticked-set changes.
  const handleRatioModeChange = useCallback((value) => {
    setRatioMode(value);
    applyRatioMode(value, submitParamsRef.current.tickedMetrics, submitParamsRef.current.excludeLossPeriods);
  }, [applyRatioMode]);

  // Yield-mode-only checkbox: opts into the same non-positive/sign-crossing
  // exclusion ratio mode always applies. Client-side rebuild, like
  // handleRatioModeChange — no refetch needed.
  const handleExcludeLossPeriodsChange = useCallback((event) => {
    const checked = event.target.checked;
    setExcludeLossPeriods(checked);
    applyExcludeLossPeriods(checked, submitParamsRef.current.tickedMetrics);
  }, [applyExcludeLossPeriods]);

  // "Adjust for SBC" checkbox (SCORE_CARDS.md §1.6). Client-side rebuild,
  // like handleExcludeLossPeriodsChange — no refetch needed.
  const handleAdjustForSbcChange = useCallback((event) => {
    const checked = event.target.checked;
    setAdjustForSbc(checked);
    applyAdjustForSbc(checked, submitParamsRef.current.tickedMetrics);
  }, [applyAdjustForSbc]);

  // Ticking/unticking a metric is purely client-side (no refetch) — the
  // hook rebuilds both charts' series from already-fetched data.
  const handleTickedMetricsChange = useCallback((next) => {
    setTickedMetrics(next);
    applyTickedMetrics(next);
  }, [applyTickedMetrics]);

  // Reads symbol/multiple/metric/valueBasis/displayScale via a ref rather
  // than closing over them directly so this callback's identity stays
  // stable across every keystroke in the search box (symbol changes on each
  // one) — otherwise ControlsPanel's React.memo is defeated on every
  // character typed. ensureCompanyName resolves the display name for a
  // typed ticker or trending selection that only carries the symbol.
  const handleSubmit = useCallback(async () => {
    const {
      symbol: sym, multiple: mult, metric: met, valueBasis: vb, displayScale: ds, axisScale: as,
      ratioMode: rm, excludeLossPeriods: elp, tickedMetrics: tm, adjustForSbc: afs,
    } = submitParamsRef.current;
    syncUrlSymbol(sym);
    const [, name] = await Promise.all([
      fetchData({
        symbol: sym, multiple: mult, companyName: sym, metric: met, valueBasis: vb, displayScale: ds, axisScale: as,
        ratioMode: rm, excludeLossPeriods: elp, tickedMetrics: tm, adjustForSbc: afs, getControls,
      }),
      ensureCompanyName(sym),
    ]);
    setCompanyNameActive(name);
  }, [fetchData, ensureCompanyName, setCompanyNameActive, syncUrlSymbol, getControls]);

  const handleSearchFocus = useCallback(() => setShowSuggestions(true), [setShowSuggestions]);
  const handleSearchClose = useCallback(() => setShowSuggestions(false), [setShowSuggestions]);

  const handleToggleControlsCollapsed = useCallback(() => {
    setControlsCollapsed((collapsed) => !collapsed);
  }, []);

  // Only rendered when there's an actual cap to apply — never while
  // collapsed (Change 1) or on the ≤768px stacked layout (see the
  // ResizeObserver effect above, which already nulls controlsMaxHeight out
  // in both those cases), so this can't fight the collapsed strip's own
  // width/height CSS.
  // Applied to .controlsContent (the inner wrapper), NOT the section itself:
  // an overflow on .controlsSection clips the collapse chevron's -14px
  // overhang, leaving it partially hidden behind the chart. Scrolling the
  // inner content keeps the section (and its seam-mounted chevron) unclipped.
  const controlsSectionStyle = (!controlsCollapsed && controlsMaxHeight != null)
    ? { maxHeight: controlsMaxHeight, overflowY: 'auto' }
    : undefined;

  const handleTrendingSelect = useCallback(async (sym) => {
    setSymbol(sym);
    syncUrlSymbol(sym);
    const [, name] = await Promise.all([
      fetchData({
        symbol: sym, multiple, companyName: sym, metric, valueBasis, displayScale, axisScale,
        ratioMode, excludeLossPeriods, tickedMetrics, adjustForSbc, getControls,
      }),
      ensureCompanyName(sym),
    ]);
    setCompanyNameActive(name);
  }, [fetchData, multiple, metric, valueBasis, displayScale, axisScale, ratioMode, excludeLossPeriods, tickedMetrics, adjustForSbc, setSymbol, setCompanyNameActive, ensureCompanyName, syncUrlSymbol, getControls]);

  return (
    <>
      <main className={styles.main}>
      <TrendingPanel items={trending} onSelect={handleTrendingSelect} loading={trendingLoading} />
      <div className={styles.content}>
      <section className={styles.chartSection} ref={chartSectionRef}>
        <h2 className={styles.chartTitle}>
          Market Value vs. Fair Value
        </h2>
        {/*
          Scoped locally rather than relying on the app-wide boundary in
          index.js: a render exception thrown by Highcharts (e.g. an
          invalid axis config) should only take out this chart, not the
          trending panel, search, controls, and stats panel that sit
          alongside it on the page.
        */}
        <ErrorBoundary message="Chart couldn't render.">
          <CustomRangeControl chartRef={chartRef} ratioChartRef={ratioChartRef} />
          <ChartPanel
            ref={chartRef}
            highcharts={Highcharts}
            chartOptions={chartOptions}
            callback={mainChartCallback}
          />
        </ErrorBoundary>

        {/*
          Centered loading overlay over the chart area itself (chartSection is
          position:relative) while stock data is being fetched — replaces the
          old inline title spinner so the "waiting" affordance sits where the
          user is looking, matching the valuation card's chart-area spinner.
        */}
        {status === 'fetching' && (
          <div className={styles.chartLoadingOverlay}>
            <Spinner size="lg" block label="Loading…" />
          </div>
        )}

        {/*
          The Change/Change Ann./Before/After stats from the click-to-measure
          gesture used to render permanently in StatsPanel's secondaryStats
          grid -- moved here into a dismissable popup that only appears once
          the gesture is complete (pointCount === 2), positioned near the
          just-drawn performance-line rather than being always-on. Rendered
          inside chartSection (which has position: relative, see
          FunViz.module.css) so its absolute-positioned backdrop/card are
          anchored to the chart area, not the whole page.
        */}
        <PerformancePopup
          pointCount={pointCount}
          before={before}
          after={after}
          change={change}
          changeAnn={changeAnn}
          displayScale={displayScale}
          resetPoints={resetPoints}
          chartRef={chartRef}
          containerRef={chartSectionRef}
        />
      </section>

      <section
        className={`${styles.controlsSection}${controlsCollapsed ? ` ${styles.controlsSectionCollapsed}` : ''}`}
      >
        {/*
          Sits on the border-left divider between the two sections,
          vertically centered — a common "collapsible sidebar" chevron
          placement. Glyph flips with state: '›' while expanded (clicking
          collapses the pane, retreating rightward/out of the way); '‹'
          while collapsed (clicking expands it, pulling it back leftward).
          Stays visible/reachable in both states since it's a direct child
          of controlsSection, not inside the content wrapper that gets
          hidden below.
        */}
        <button
          type="button"
          className={styles.collapseToggle}
          onClick={handleToggleControlsCollapsed}
          aria-expanded={!controlsCollapsed}
          aria-label={controlsCollapsed ? 'Expand controls panel' : 'Collapse controls panel'}
        >
          {controlsCollapsed ? '‹' : '›'}
        </button>

        {/*
          Hidden via CSS (display:none through controlsContentHidden), never
          unmounted -- SymbolSearch/ControlsPanel/StatsPanel stay mounted
          while collapsed so no in-progress state inside them (e.g. typed
          search text) is lost.
        */}
        <div
          className={`${styles.controlsContent}${controlsCollapsed ? ` ${styles.controlsContentHidden}` : ''}`}
          style={controlsSectionStyle}
        >
          <div className={styles.inputControls}>
            <SymbolSearch
              containerRef={searchRef}
              symbol={symbol}
              suggestions={suggestions}
              showSuggestions={showSuggestions}
              onSymbolChange={handleSymbolChange}
              onSuggestionSelect={handleSuggestionSelect}
              onFocus={handleSearchFocus}
              onClose={handleSearchClose}
              onSubmit={handleSubmit}
              status={status}
            />
            {searchFailed && (
              <p className={styles.searchUnavailable} role="alert">
                Search unavailable — please try again.
              </p>
            )}

            <ControlsPanel
              metric={metric}
              multipleMode={multipleMode}
              multipleValue={multiple}
              multipleDefault={defaultMultiple(metric, valueBasis)}
              valueBasis={valueBasis}
              displayScale={displayScale}
              axisScale={axisScale}
              adjustForSbc={adjustForSbc}
              onMetricChange={handleMetricChange}
              onMultipleCommit={handleMultipleCommit}
              onValueBasisChange={handleValueBasisChange}
              onDisplayScaleChange={handleDisplayScaleChange}
              onAxisScaleChange={handleAxisScaleChange}
              onAdjustForSbcChange={handleAdjustForSbcChange}
            />
          </div>

          {status === 'error' && (
            <p className={styles.fetchError} role="alert">
              Couldn't load data for that symbol — please try again.
            </p>
          )}

          <StatsPanel
            companyName={companyNameActive}
            symbol={symbolActive}
            metric={metric}
            valueBasis={valueBasis}
            sector={profileActive.sector}
            industry={profileActive.industry}
            country={profileActive.country}
            currentRatio={keyFacts.currentRatio}
            dividendYield={keyFacts.dividendYield}
          />
        </div>
      </section>
      </div>
      </main>

      {/* Secondary cards collapse to a title bar and lazy-mount their body on
          expand (so a collapsed card's chart never mounts). The Ratio card is
          open by default — it also keeps ratioChartRef live for the main
          chart's axis sync from first load. Scores starts collapsed, with its
          own chart ref so a zoom there does not move the main chart or the
          ratio chart. The Valuation and Sankey cards
          start collapsed; the Valuation card reports its open state up
          (onToggle) so the two regression hooks above stay gated. */}
      <CollapsibleCard title="Ratio / Yield" storageKey="funviz.card.ratio" defaultOpen>
        <RatioChartCard
          embedded
          ref={ratioChartRef}
          highcharts={Highcharts}
          chartOptions={ratioChartOptions}
          callback={ratioChartCallback}
          ratioMode={ratioMode}
          onRatioModeChange={handleRatioModeChange}
          excludeLossPeriods={excludeLossPeriods}
          onExcludeLossPeriodsChange={handleExcludeLossPeriodsChange}
          tickedMetrics={tickedMetrics}
          onTickedMetricsChange={handleTickedMetricsChange}
        />
      </CollapsibleCard>

      <CollapsibleCard title="Scores" storageKey="funviz.card.scores" defaultOpen={false}>
        <ScoresCard
          embedded
          highcharts={Highcharts}
          price={valuationInputs.price}
          shares={valuationInputs.shares}
          netDebt={valuationInputs.netDebt}
          revenue={valuationInputs.metricSeriesByMetric.revenue}
          ebitda={valuationInputs.metricSeriesByMetric.ebitda}
          fcf={valuationInputs.metricSeriesByMetric.fcf}
          qualityInputs={qualityInputs}
          incomeAnnual={incomeStatement.annual}
          ratios={ratios}
          valueBasis={valueBasis}
          displayScale={displayScale}
          defaultRange={defaultRange}
        />
      </CollapsibleCard>

      <CollapsibleCard
        title="Valuation vs. Subsequent Returns"
        storageKey="funviz.card.valuation"
        defaultOpen={false}
        onToggle={setValuationOpen}
      >
        <ValuationSubsequentReturnsCard
          embedded
          horizon={horizon}
          onHorizonChange={setHorizon}
          lookback={lookback}
          onLookbackChange={setLookback}
          resultsByHorizon={resultsByHorizon}
          metric={valuationMetric}
          onMetricChange={handleValuationMetricChange}
          period={valuationPeriod}
          onPeriodChange={handleValuationPeriodChange}
          valueBasis={valueBasis}
          correlationRows={correlationRows}
        />
      </CollapsibleCard>

      <CollapsibleCard title="Income Flow" storageKey="funviz.card.sankey" defaultOpen={false}>
        <IncomeStatementSankeyCard
          embedded
          periods={incomePeriods}
          currency={incomeStatement.currency}
          cadence={incomeCadence}
          onCadenceChange={handleIncomeCadenceChange}
          index={incomeIndex}
          onSelect={handleIncomeSelect}
          onPrev={handleIncomePrev}
          onNext={handleIncomeNext}
          symbol={symbolActive}
        />
      </CollapsibleCard>

      <DisclaimerFooter />
    </>
  );
};

export default Analysis;
